import React from "react";

import Panel from "../panels/Panel";
import Grid, { GridElements } from "../grid/Grid";
import Button from "../buttons/Button";
import Dialog from "../dialog/Dialog";
import DialogContent from "../dialog/DialogContent";
import DialogFooter from "../dialog/DialogFooter";

import "./SortDialog.scss";
import PanelHeader from "../panels/PanelHeader";
import IconStyles from "../../types/enums/IconStyles";
import GridColumn from "../grid/GridColumn";
import Utility from "../../foundation/Utility";
import DialogOK from "../dialog/DialogOK";
import DialogCancel from "../dialog/DialogCancel";

interface SortDialogProps<T> {
    value: T[];
    keyField: string;
    showDialog?: boolean;
    showLoading?: boolean;

    children?:
        | React.ReactElement<GridColumn<T>>
        | React.ReactElement<GridColumn<T>>[];

    onSave?: (value: T[]) => void;
    onCancel?: () => void;
}

interface SortDialogState<T> {
    value: T[];
    record?: T;
    column?: GridColumn<T>;
}

class SortDialog<T> extends React.Component<
    SortDialogProps<T>,
    SortDialogState<T>
> {
    state = {
        value: [],
    } as SortDialogState<T>;

    componentDidMount(): void {
        this.setState(() => ({ value: this.props.value }));
    }

    componentDidUpdate(prevProps: Readonly<SortDialogProps<T>>): void {
        if (prevProps.value !== this.props.value) {
            this.setState(() => ({ value: this.props.value.slice() }));
        }
    }

    selectionChanged(record: T, _: T[], column: GridColumn<T>): void {
        this.setState(() => ({ record, column }));
    }

    sortAscending() {
        if (!this.state.column) return;

        this.setState(state => ({
            value: state.value
                .slice()
                .sort(Utility.getSorter([this.state.column!.field])),
        }));
    }

    sortDescending() {
        if (!this.state.column) return;

        this.setState(state => ({
            value: state.value
                .slice()
                .sort(Utility.getSorter([`-${this.state.column!.field}`])),
        }));
    }

    moveUp() {
        if (!this.state.record) return;

        const index = this.state.value.indexOf(this.state.record);
        if (index > 0) {
            const newValue = this.state.value.slice();
            [newValue[index - 1], newValue[index]] = [
                newValue[index],
                newValue[index - 1],
            ];

            this.setState(() => ({ value: newValue }));
        }
    }

    moveDown() {
        if (!this.state.record) return;

        const index = this.state.value.indexOf(this.state.record);
        if (index < this.state.value.length - 1) {
            const newValue = this.state.value.slice();
            [newValue[index + 1], newValue[index]] = [
                newValue[index],
                newValue[index + 1],
            ];

            this.setState(() => ({ value: newValue }));
        }
    }

    reset() {
        this.setState(() => ({ value: this.props.value.slice() }));
    }

    render(): React.ReactNode {
        return (
            <Dialog
                dialogIcon="sort"
                dialogTitle="Sort"
                showDialog={this.props.showDialog}
                showLoading={this.props.showLoading}
                width={850}>
                <DialogContent>
                    <Panel>
                        <PanelHeader spaced={true}>
                            <Button
                                icon="sort"
                                tooltip="Sort column ascending"
                                tooltipPlace="top"
                                iconStyle={IconStyles.ReversedY}
                                text="Sort Ascending"
                                disabled={!this.state.column}
                                onClick={this.sortAscending.bind(this)}
                            />
                            <Button
                                icon="sort"
                                tooltip="Sort column descending"
                                tooltipPlace="top"
                                text="Sort Descending"
                                disabled={!this.state.column}
                                onClick={this.sortDescending.bind(this)}
                            />
                            <Button
                                icon="arrow_upward"
                                tooltip="Move record up"
                                tooltipPlace="top"
                                text="Move Up"
                                disabled={!this.state.record}
                                onClick={this.moveUp.bind(this)}
                            />
                            <Button
                                icon="arrow_downward"
                                tooltip="Move record down"
                                tooltipPlace="top"
                                text="Move Down"
                                disabled={!this.state.record}
                                onClick={this.moveDown.bind(this)}
                            />
                            <Button
                                icon="history"
                                tooltip="Reset sorting"
                                tooltipPlace="top"
                                text="Reset"
                                onClick={this.reset.bind(this)}
                            />
                        </PanelHeader>

                        <Grid<T>
                            globalSelect={true}
                            value={this.state.value}
                            keyField={this.props.keyField}
                            selectionChanged={this.selectionChanged.bind(this)}
                            fixedHeader={true}
                            maxHeight="400px">
                            {this.props.children}
                        </Grid>
                    </Panel>
                </DialogContent>
                <DialogFooter>
                    <DialogCancel onClick={() => this.props.onCancel?.()} />
                    <DialogOK
                        text="Save"
                        onClick={() => this.props.onSave?.(this.state.value)}
                    />
                </DialogFooter>
            </Dialog>
        );
    }
}

export default SortDialog;
